/**
 * `useStickToBottom` — keep a scroll container pinned to its bottom across
 * REFLOWS, without fighting a user who has deliberately scrolled up.
 *
 * Why this exists: the conversation body is a plain `overflow-auto` div with no
 * scroll logic, relying on the browser's native scroll anchoring. Anchoring
 * copes with content being appended; it does NOT cope with the container
 * CHANGING WIDTH. Narrowing the conversation (opening or resizing the focus
 * pane, toggling a sidebar, switching a case) re-wraps every message, so
 * `scrollHeight` grows while `scrollTop` stays put — and the reader is left
 * short of the bottom. Measured: widening the focus pane to 65% grew the body
 * 43286 → 46265px and stranded the view 27px up, which reads as "it never quite
 * scrolls to the end".
 *
 * Behaviour:
 *   - "At bottom" is a tolerance, not equality — sub-pixel layout and rounding
 *     mean an exact match is unreliable.
 *   - We only re-pin if the user WAS at the bottom before the reflow. Scrolled
 *     up to read something? Nothing moves under you.
 *   - Both the container and its content are observed: a width change resizes
 *     the container, a re-wrap resizes the content, and either can strand you.
 */
import { useEffect, useRef, type RefObject } from "react";

/** Within this many px of the bottom still counts as "pinned". */
const AT_BOTTOM_TOLERANCE_PX = 32;

export function useStickToBottom(ref: RefObject<HTMLElement | null>): void {
  // Whether the user was at the bottom BEFORE the current reflow. Kept in a
  // ref so reading it during a resize callback never sees a stale render.
  const wasAtBottomRef = useRef(true);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const distanceFromBottom = () =>
      el.scrollHeight - el.clientHeight - el.scrollTop;

    // ⚠ THIS HOOK IS THE ONLY THING THAT SCROLLS THE TRANSCRIPT.
    //
    // It used to share the job with a `scrollIntoView` effect in
    // `ChatMessageList`, and the two fought: that one animates (smooth) and
    // lands where the bottom was when it STARTED, so on a turn whose tail is
    // still growing it settles SHORT — which the old listener read as "the
    // reader left the bottom", latching the pin off for the rest of the turn.
    // Then the reverse: any pin we performed fired a scroll event that read as
    // "back at the bottom" and re-armed it under a reader who had deliberately
    // scrolled away. Operator saw both faces of this — the pane refusing to
    // follow new rows, then jerking back down while they were reading.
    //
    // One owner, and a flag that only listens to scrolls WE DID NOT CAUSE.
    let pinning = false;

    const onScroll = () => {
      if (pinning) return; // our own pin proves nothing about intent
      wasAtBottomRef.current = distanceFromBottom() <= AT_BOTTOM_TOLERANCE_PX;
    };
    el.addEventListener("scroll", onScroll, { passive: true });

    const detachListeners = () => el.removeEventListener("scroll", onScroll);

    if (typeof ResizeObserver === "undefined") {
      return detachListeners;
    }

    const repin = () => {
      if (!wasAtBottomRef.current) return;
      // After a reflow the browser may still be settling layout; scrolling on
      // the next frame lands on the final scrollHeight rather than a stale one.
      pinning = true;
      requestAnimationFrame(() => {
        if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
        // Release only after the resulting scroll event has been delivered,
        // so our own movement never re-arms the flag.
        requestAnimationFrame(() => {
          pinning = false;
        });
      });
    };

    const ro = new ResizeObserver(repin);
    ro.observe(el);
    // The content wrapper: a width change re-wraps text, which changes the
    // content height without changing the container's.
    const content = el.firstElementChild;
    if (content) ro.observe(content);

    // Rows append as NEW ELEMENTS. A ResizeObserver on the container + its first
    // child can miss growth that layout absorbs, so "the log printed something"
    // becomes a first-class trigger instead of something inferred from a box
    // measurement. Still gated on `wasAtBottomRef` inside `repin`, so a
    // scrolled-up reader is never moved.
    const mo = new MutationObserver(repin);
    mo.observe(el, { childList: true, subtree: true, characterData: true });

    return () => {
      detachListeners();
      ro.disconnect();
      mo.disconnect();
    };
  }, [ref]);
}
